// "Submission" — Practice Details card for the submit queue (Instance B, phase 3).
//
// Read-only. It says whether this practice has the queue and why, which portal the
// skill works and at what address, which runbook version it would run and whether a
// person has approved it, and where the skill's key comes from. Nothing is edited
// here: the Auto/On/Off control is on Practice Details' Features tab, and the runbook
// is edited on the Submit queue page. Shown on the Integrations tab (2026-10-25).
//
// The portal URL comes from the runbook's structured half (`base_url`, C's schema),
// which a skill run can write. It is shown as text, never as a link.

import React, { useEffect, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
import { fetchRunbook } from "./api";
import { relativeTime } from "./ageing";
import "./Queue.css";

const UNKNOWN_RE = /UNKNOWN/;

// How the gate arrived at its answer, in words. `overrides.submit_queue` is true or
// false when an admin forced it; absent means Auto.
export function queueGateState({ features, overrides, computed, source }) {
  const on = !!features.submit_queue;
  const forced = overrides && "submit_queue" in overrides;
  const sourceLabel = source?.label || source?.key || "this practice's source";
  if (forced) {
    return {
      on,
      mode: on ? "On (forced)" : "Off (forced)",
      why: on
        ? "An admin turned the queue on for this practice."
        : "An admin turned the queue off for this practice.",
    };
  }
  return {
    on,
    mode: on ? "Auto — on" : "Auto — off",
    why:
      computed && computed.submit_queue === false
        ? `${sourceLabel} submits claims itself, so Atlas does not queue them.`
        : `${sourceLabel} does not submit claims, so Atlas keeps the record of what was sent.`,
  };
}

export default function SubmissionCard({ practiceId }) {
  const gate = useCapabilities(practiceId);
  const [lineages, setLineages] = useState(null);
  const [error, setError] = useState(null);
  const on = !!gate.features.submit_queue;

  useEffect(() => {
    // The runbook routes 404 when the feature is off; do not ask.
    if (!practiceId || !on) {
      setLineages(null);
      return undefined;
    }
    let cancelled = false;
    setError(null);
    setLineages(null);
    (async () => {
      try {
        const first = await fetchRunbook(practiceId);
        let found;
        if (first.portal || first.portals.length === 0) {
          found = first.portal ? [first] : [];
        } else {
          // Several lineages: one read per portal, each practice-scoped.
          found = await Promise.all(
            first.portals.map((portal) => fetchRunbook(practiceId, portal)),
          );
        }
        if (!cancelled) setLineages(found);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, on]);

  if (!practiceId || gate.loading) return null;
  if (gate.error) {
    return (
      <div className="integrations-section sq-card">
        <h3>Submission</h3>
        <div className="sq-message error">{gate.error}</div>
      </div>
    );
  }

  const state = queueGateState(gate);

  return (
    <div className="integrations-section sq-card">
      <h3>Submission</h3>

      <table className="detail-table sq-card-table">
        <tbody>
          <tr>
            <td className="detail-label">Submit queue</td>
            <td>
              <span
                className={`sq-status ${state.on ? "sq-status-submitted" : "sq-status-removed"}`}
              >
                {state.mode}
              </span>
              <div className="sq-muted sq-card-why">
                {state.why} Change it in the <strong>Submit queue</strong> row
                of the Features tab.
              </div>
            </td>
          </tr>

          {state.on && (
            <tr>
              <td className="detail-label">Skill key</td>
              <td>
                The Cowork skill needs a <code>queue:work</code> API key for
                this practice. An Atlas admin issues it in the{" "}
                <strong>API keys</strong> section of this page, where it is
                shown once. It lives in{" "}
                <code>ATLAS_API_KEY</code> on the machine that runs the skill,
                never in chat or on a page.
              </td>
            </tr>
          )}

          {state.on && error && (
            <tr>
              <td className="detail-label">Runbook</td>
              <td>
                <div className="sq-message error">{error}</div>
              </td>
            </tr>
          )}

          {state.on && !error && lineages === null && (
            <tr>
              <td className="detail-label">Runbook</td>
              <td className="sq-muted">Loading…</td>
            </tr>
          )}

          {state.on && lineages && lineages.length === 0 && (
            <tr>
              <td className="detail-label">Runbook</td>
              <td className="sq-muted">
                No runbook yet — the first skill run writes one.
              </td>
            </tr>
          )}

          {state.on &&
            lineages &&
            lineages.map((lineage) => {
              const latest = lineage.latest;
              const baseUrl =
                latest &&
                latest.structured &&
                typeof latest.structured.base_url === "string"
                  ? latest.structured.base_url
                  : null;
              return (
                <tr key={lineage.portal}>
                  <td className="detail-label">
                    Portal <code>{lineage.portal}</code>
                  </td>
                  <td>
                    <div>
                      Address:{" "}
                      {baseUrl && !UNKNOWN_RE.test(baseUrl) ? (
                        <code className="sq-card-url">{baseUrl}</code>
                      ) : (
                        <span className="sq-muted">
                          not known yet
                          {baseUrl ? " (the runbook marks it UNKNOWN)" : ""}
                        </span>
                      )}
                    </div>
                    {latest ? (
                      <div>
                        Runbook version {latest.version}
                        {" · "}
                        {latest.approvedBy ? (
                          <span className="sq-status sq-status-submitted">
                            approved by {latest.approvedBy}
                            {latest.approvedAt
                              ? ` ${relativeTime(latest.approvedAt)}`
                              : ""}
                          </span>
                        ) : (
                          <span className="sq-status sq-status-removed">
                            unapproved
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="sq-muted">No version saved.</div>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      {state.on && (
        <p className="sq-muted sq-card-foot">
          The runbook, the queue and the copyable skill text are on the{" "}
          <strong>Submit queue</strong> page in the sidebar.
        </p>
      )}
    </div>
  );
}
